/**
 * Conductores — usuarios del operador logístico, en 2 segmentos.
 *
 * Entiende a los usuarios como 2 grupos, según `role.name` real del backend:
 *   1) Choferes        → delivery, delivery_freelance, delivery_commerce_fleet
 *   2) Gestión interna → administrator, supervisor, support, superadmin, holdingAdmin
 *
 * Los usuarios de clientes/comercios (key.account.manager, client) viven en el
 * hub Empresas → tab Cuentas (/admin/contracts?tab=cuentas); el ?tab=clientes
 * histórico y los deep-links ?user= de un usuario de comercio redirigen allá.
 *
 * Datos REALES (no se toca la capa de queries): se traen los usuarios activos del
 * tenant (users/status/user por logisticCompanyId) + los choferes pendientes
 * (getInactiveDeliverers), y se bucketean por rol en el front. Ver/editar/
 * bloquear/eliminar REUSAN los modales existentes (mismos que la vista vieja).
 *
 * "Gestión interna" ya NO es de solo lectura: se invita por email a colegas de
 * back-office contra `users/logistic-team` — mismo mecanismo que el equipo de
 * comercio. Se lista de ese endpoint (que trae activos, pendientes e inactivos)
 * en vez de bucketear el listado de activos del tenant, que dejaba fuera a los
 * invitados sin aceptar.
 *
 * El alta de choferes NO se hace desde acá: el botón "Crear usuario" enlaza al
 * registro público co-brandeado (`?from=<website del operador>`), que es el
 * flujo que captura los datos propios de cada uno. El modal de chofer sigue
 * montado porque es el mismo que se usa para EDITAR.
 *
 * "Roles y permisos" (pestaña visible con `role:read`) y "Cambiar rol" (con
 * `role:assign`) hablan con microservice-users (R3.2); ver ./roles/.
 * Lo que el backend NO soporta aún (2FA, hubs por usuario, último acceso) sigue
 * con PendingBackendNotice y datos atenuados, sin inventar. Ver
 * /home/superadmin/USUARIOS-BACKEND.md.
 *
 * La vista vieja (beartrackUsers.jsx) quedó SIN RUTA: /admin/users-old se
 * eliminó por desuso (2026-08-08). El código sigue en el repo como referencia.
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Truck, Shield, Users as UsersIcon, Plus, Search, Eye, Pencil,
  Lock, Unlock, Trash2, MoreVertical, ShieldCheck, UserPlus, Layers, Check,
  Send, Play, Pause, Clock, ExternalLink, UserCheck, UserCog,
} from "lucide-react";
import { toast } from "../../../utils/toast";
import useAuth from "../../../hooks/useAuth";
import { usePermission } from "../../../hooks/usePermission";
import { useIsMobile } from "../../../hooks/useIsMobile";
import useUrlState, { useUrlStateBatch } from "../../../hooks/useUrlState";
import { getDeliveryUsers, getInactiveDeliverers } from "../../../api/user";
import {
  getLogisticTeam,
  createLogisticTeamUser,
  activateLogisticTeamUser,
  deactivateLogisticTeamUser,
  resendLogisticTeamInvite,
  invitableRolesFor,
} from "../../../api/logisticTeam";
import { getLogisticsCompany } from "../../../api/settings";
import { roleRoutes } from "../../../utils/roles";
import { PageHeader } from "../../../components/ui/PageHeader";
import { Card } from "../../../components/ui/Card";
import { Badge } from "../../../components/ui/Badge";
import { Avatar } from "../../../components/ui/Avatar";
import { Button } from "../../../components/ui/Button";
import { EmptyState } from "../../../components/ui/EmptyState";
import { TableSkeleton } from "../../../components/ui/TableSkeleton";
import { PendingBackendNotice } from "../../../components/ui/PendingBackendNotice";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../components/ui/Tabs";
import { Table, THead, TBody, TR, TH, TD } from "../../../components/ui/Table";
import { Modal as UiModal } from "../../../components/ui/Modal";
import { Input } from "../../../components/ui/Input";
import { Select } from "../../../components/ui/Select";
import { SortControl } from "../../../components/ui/SortControl";
import { FieldLabel } from "../../../components/ui/FieldLabel";
import Modal from "../../../components/modal";
import PackageTabs from "../../../components/packageTabs";
import CreateDeliveryBeartrack from "../../../components/createDeliveryBeartrack/createDeliveryBeartrack";
import BlockDeleteUserBeartrack from "../../../components/blockDeleteUserBeartrack/blockDeleteUserBeartrack";
import EnableUserModal from "../../../components/enableUserModal/EnableUserModal";
import { cn } from "../../../utils/cn";
import { formatRelative } from "../../../utils/formatters";
import { driverVehicleLabel } from "../../../utils/driverVehicleTypes";
import { apiBodyMessage, apiErrorMessage, mapValidationErrors } from "../../../utils/apiErrorMessage";
import UserDetail from "./UserDetail";
import {
  roleSegment, teamStatus, roleTone, initialsOf, humanizeRole, buildRegisterUrl,
} from "./usersConfig";
import RolesPermisos from "./roles/RolesPermisos";
import ChangeRoleModal from "./roles/ChangeRoleModal";

// Tabs válidos (?tab=). Un valor fuera de este set dejaría el cuerpo sin
// TabsContent (panel en blanco), así que lo normalizamos al primero ("choferes").
// "roles" (Roles y permisos) solo es válido si el token trae `role:read`.
const VALID_TABS = ["choferes", "interna", "roles"];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INITIAL_INVITE_FORM = { name: "", lastName: "", email: "", phone: "", role: "" };

// Largos que acepta el backend (CreateLogisticTeamUserDto). Se validan también
// acá para no mandar un alta que ya sabemos que va a rebotar con 400.
const INVITE_NAME_MAX = 80;
const INVITE_PHONE_MIN = 6;
const INVITE_PHONE_MAX = 30;

/**
 * Restricción de class-validator ("phone must be longer than or equal to 6
 * characters") → texto para el input. El primer token es la propiedad y se
 * descarta: el error ya se pinta debajo de su campo. Lo que no calza con
 * ninguna restricción conocida (p. ej. el mensaje de `role`, que el backend ya
 * manda en español) se devuelve crudo, sin inventarle traducción.
 * @param {string} message - mensaje crudo del backend
 * @param {(key: string, opts?: object) => string} t
 * @returns {string}
 */
function translateInviteConstraint(message, t) {
  const rest = String(message || "").replace(/^\S+\s*/, "").trim();
  const min = rest.match(/^must be longer than or equal to (\d+) characters$/);
  if (min) return t("Mínimo {{min}} caracteres", { min: min[1] });
  const max = rest.match(/^must be shorter than or equal to (\d+) characters$/);
  if (max) return t("Máximo {{max}} caracteres", { max: max[1] });
  if (/^must be an email$/.test(rest)) return t("Email inválido");
  if (/^should not be empty$/.test(rest)) return t("Este dato es obligatorio.");
  return message;
}

// roleId del JWT → `role.name` REAL del backend (verificado contra la colección
// `roles`: el supervisor está guardado como "supervisory", no "supervisor").
// El `systemSuperadmin` queda fuera a propósito: no tiene logisticCompanyId, así
// que no puede gestionar el equipo de ningún operador (403 fail-closed).
const ROLE_NAME_BY_ID = {
  [roleRoutes.admin]: "administrator",
  [roleRoutes.superadmin]: "superadministrator",
  [roleRoutes.supervisor]: "supervisory",
  [roleRoutes.support]: "support",
};

function FormField({ label, required, error, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel>
        {label}
        {required && <span className="text-status-error"> *</span>}
      </FieldLabel>
      {children}
      {error ? <span className="text-xs text-status-error">{error}</span> : null}
    </div>
  );
}

// ── KPI / Stat card ──
// Anatomía del primitivo ui/Stat (port 1:1 del mockup mu): micro-label
// uppercase + chip de ícono arriba-derecha + valor grande + hint. Copia local
// y NO KpiFilterCards porque estos KPIs son toggles heterogéneos (segmento /
// activos / por aceptar), no un grupo de filtro exclusivo, y la estética
// objetivo es la del Stat del mockup.
function Stat({ label, value, hint, icon: Icon, tone = "brand", active, onClick }) {
  const toneCls = {
    brand: "text-brand-700 bg-brand-50",
    success: "text-status-success bg-status-success-soft",
    warning: "text-status-warning bg-status-warning-soft",
    info: "text-status-info bg-status-info-soft",
    neutral: "text-fg-muted bg-bg-muted",
  }[tone] || "text-brand-700 bg-brand-50";
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      type={onClick ? "button" : undefined}
      onClick={onClick}
      aria-pressed={onClick ? !!active : undefined}
      className={cn(
        "rounded-xl border bg-bg p-4 text-left shadow-soft transition-colors",
        onClick &&
          "cursor-pointer hover:border-brand-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
        active ? "border-brand-500 ring-1 ring-brand-500" : "border-border",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs font-medium text-fg-muted uppercase tracking-wide">{label}</span>
        {Icon && (
          <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg", toneCls)}>
            <Icon className="w-4 h-4" />
          </span>
        )}
      </div>
      <div className="mt-2 text-2xl font-semibold text-fg tabular-nums">{value}</div>
      {hint && <div className="mt-1 text-xs text-fg-muted">{hint}</div>}
    </Comp>
  );
}

export default function UsersModule() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const logisticCompanyId = useMemo(
    () => user?.logisticCompany?._id || user?.logisticCompanyId || null,
    [user]
  );

  // Estado de UI persistido en la URL (compartible / back-forward). Mismos defaults
  // que antes ⇒ sin params en la URL el comportamiento es IDÉNTICO al actual.
  const [tabRaw] = useUrlState("tab", "choferes", { push: true }); // tabs ⇒ push (back vuelve al tab anterior)
  // Permisos del token: deciden qué se ofrece (la autorización real es del backend).
  const canReadRoles = usePermission("role:read");
  const canAssignRole = usePermission("role:assign");
  // ?tab inválido (o "roles" sin `role:read`) ⇒ primer tab (evita cuerpo en blanco)
  const tab = VALID_TABS.includes(tabRaw) && (tabRaw !== "roles" || canReadRoles) ? tabRaw : "choferes";
  // Compatibilidad: el segmento Clientes vive ahora en Empresas → Cuentas. Un
  // ?tab=clientes viejo redirige allá (preservando ?user=); el flag además
  // gatea el fetch para no disparar los GETs de este módulo antes del unmount.
  const legacyClientesTab = tabRaw === "clientes";
  const [all, setAll] = useState([]);        // activos del tenant
  // Equipo interno del operador (activos + pendientes + inactivos). `null` =
  // el endpoint no respondió: se cae al bucketeo del listado de activos y se
  // ocultan las acciones de gestión, para no ofrecer botones que fallarían.
  const [internos, setInternos] = useState(null);
  // `website` del operador, para co-brandear los enlaces de registro público.
  // No viaja en el JWT, así que se lee de la empresa.
  const [operatorWebsite, setOperatorWebsite] = useState("");
  const [pending, setPending] = useState([]); // choferes por aceptar
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useUrlState("q", "");
  const [roleFilter, setRoleFilter] = useUrlState("role", ""); // sub-filtro por role.name dentro del segmento
  const [pendingMode, setPendingMode] = useUrlState("pending", false, { type: "boolean" }); // choferes: ver pendientes
  // Filtro de estado client-side ("" | active | blocked | pending). El Stat
  // "Activos" es un atajo que lo alterna a "active"; el Select de estado del
  // toolbar lo maneja completo. "pending" solo aplica a Gestión interna
  // (invitado sin aceptar, vía teamStatus).
  const [estadoFilter, setEstadoFilter] = useState("");
  const activeOnly = estadoFilter === "active";
  // Orden client-side de la lista visible ("" = orden de llegada del backend).
  const [userSortField, setUserSortField] = useState(""); // "" | name | created
  const [userSortDir, setUserSortDir] = useState("desc");

  // Setter atómico para cambiar/resetear varios params en UNA navegación
  // (en react-router v6 dos setSearchParams encadenados en el mismo handler se pisan).
  const setUserParams = useUrlStateBatch(
    {
      tab: { defaultValue: "choferes" },
      q: { defaultValue: "" },
      role: { defaultValue: "" },
      pending: { type: "boolean", defaultValue: false },
    },
    { push: true }
  );

  // Detalle inline (?user=<id>): al clickear una fila la lista se reemplaza
  // por UserDetail (mismo patrón que el hub Empresas). push ⇒ el botón Volver
  // del navegador regresa a la lista.
  const [detailUserId, setDetailUserId] = useUrlState("user", "", { push: true });

  // Modales (mismo cableado que la vista vieja)
  const [selectedUser, setSelectedUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [blockMode, setBlockMode] = useState(false);
  const [openDelivery, setOpenDelivery] = useState(false);
  const [openBlock, setOpenBlock] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [rowMenu, setRowMenu] = useState(null);
  // Coordenadas fixed del menú de fila. El dropdown se portalea a <body> porque
  // la Card (overflow-hidden) y el wrapper overflow-x-auto lo recortan cuando la
  // tabla tiene pocas filas (p. ej. una búsqueda con un único resultado).
  const [rowMenuPos, setRowMenuPos] = useState(null);

  // Dropdown "Crear usuario": vive en el header del Card de la tabla (patrón
  // mockup) y se PORTALEA con posición fixed — el Card lleva overflow-hidden y
  // recortaría un menú absolute cuando la tabla es corta (empty state, mobile).
  const [createMenuPos, setCreateMenuPos] = useState(null);
  const toggleCreateMenu = (e) => {
    if (openCreate) {
      setOpenCreate(false);
      return;
    }
    const r = e.currentTarget.getBoundingClientRect();
    // clientWidth y no innerWidth: `right:` fixed se mide contra el layout
    // viewport (sin scrollbar); con innerWidth el menú quedaba corrido ~15px.
    setCreateMenuPos({
      top: r.bottom + 4,
      right: Math.max(8, document.documentElement.clientWidth - r.right),
    });
    setOpenCreate(true);
  };

  const openRowMenu = (e, id) => {
    e.stopPropagation();
    if (rowMenu === id) { setRowMenu(null); return; }
    const r = e.currentTarget.getBoundingClientRect();
    const MENU_W = 208; // w-52
    const MENU_H = 340; // alto máximo estimado (hasta ~8 ítems)
    const left = Math.max(8, r.right - MENU_W);
    const openUp = r.bottom + MENU_H > window.innerHeight && r.top > MENU_H;
    setRowMenuPos(openUp
      ? { left, bottom: window.innerHeight - r.top + 4 }
      : { left, top: r.bottom + 4 });
    setRowMenu(id);
  };
  // Habilitar usuario (`active: true`): "accept" para la cola de pendientes,
  // "unblock" para un usuario ya vinculado. null ⇒ modal cerrado.
  const [enableMode, setEnableMode] = useState(null);

  // Invitación al equipo interno
  const [openInvite, setOpenInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState(INITIAL_INVITE_FORM);
  const [inviteErrors, setInviteErrors] = useState({});
  const [inviting, setInviting] = useState(false);
  const [busyId, setBusyId] = useState(null);
  // Integrante del equipo al que se le está cambiando el rol (null = modal cerrado).
  const [roleChangeTarget, setRoleChangeTarget] = useState(null);

  const registerUrl = useCallback(
    (kind) => buildRegisterUrl(kind, operatorWebsite),
    [operatorWebsite],
  );

  const callerRoleName = ROLE_NAME_BY_ID[user?.role] || "";
  const assignableRoles = useMemo(() => invitableRolesFor(callerRoleName), [callerRoleName]);
  // Gestiona el equipo quien puede asignar algún rol y tiene operador asociado,
  // siempre que el endpoint esté respondiendo.
  const canManageTeam = assignableRoles.length > 0 && !!logisticCompanyId && internos !== null;

  // ── Fetch real ──
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [activeRes, pendingRes, internalRes, companyRes] = await Promise.all([
        getDeliveryUsers("active", undefined, logisticCompanyId),
        getInactiveDeliverers(),
        // Equipo interno: trae también a los invitados sin aceptar y a los
        // desactivados, que el listado de activos del tenant no incluye.
        getLogisticTeam(),
        // Solo por el `website` (co-branding de los enlaces de registro). Es
        // accesorio: si falla, los enlaces caen al registro genérico y la
        // pantalla no se rompe.
        getLogisticsCompany(logisticCompanyId).catch(() => null),
      ]);
      const docs = activeRes?.docs || activeRes?.data?.docs || (Array.isArray(activeRes) ? activeRes : []);
      setAll(Array.isArray(docs) ? docs : []);
      const pdocs = pendingRes?.docs || pendingRes || [];
      setPending(Array.isArray(pdocs) ? pdocs.filter((u) => roleSegment((u?.role?.name)) === "choferes") : []);
      setInternos(Array.isArray(internalRes?.data) ? internalRes.data : null);
      setOperatorWebsite(companyRes?.data?.website || "");
    } catch (e) {
      console.error("UsersModule fetch", e);
      toast.error(t("Error al cargar usuarios"));
    } finally {
      setLoading(false);
    }
  }, [logisticCompanyId, t]);

  useEffect(() => {
    if (logisticCompanyId && !legacyClientesTab) fetchAll();
  }, [logisticCompanyId, fetchAll, legacyClientesTab]);

  // Cerrar menús al click afuera
  useEffect(() => {
    const onClick = (e) => {
      if (openCreate && !e.target.closest(".um-create")) setOpenCreate(false);
      if (rowMenu && !e.target.closest(".um-rowmenu")) setRowMenu(null);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [openCreate, rowMenu]);

  // El menú de fila queda anclado a coordenadas fixed: cualquier scroll/resize
  // lo desalinearía, así que se cierra (mismo criterio que un dropdown nativo).
  useEffect(() => {
    if (!rowMenu) return;
    const close = () => setRowMenu(null);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [rowMenu]);

  // Mismo criterio para el dropdown "Crear usuario" (también fixed-portaleado).
  useEffect(() => {
    if (!openCreate) return;
    const close = () => setOpenCreate(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [openCreate]);

  // ── Buckets por segmento ──
  // La key "clientes" se conserva aunque el tab ya no exista: los KAM/client
  // del tenant siguen viniendo en `all` y caerían en acc[seg] — su
  // administración vive en Empresas → Cuentas (ver redirects del render).
  const bySegment = useMemo(() => {
    const acc = { choferes: [], clientes: [], interna: [], otros: [] };
    for (const u of all) {
      const seg = roleSegment(u?.role?.name) || "otros";
      acc[seg].push(u);
    }
    // Gestión interna: la fuente autoritativa es users/logistic-team, que ya
    // viene scopeada al operador y filtrada por rol server-side, e incluye
    // pendientes e inactivos. El bucketeo de `all` queda solo como respaldo si
    // ese endpoint no respondió.
    if (internos) acc.interna = internos;
    return acc;
  }, [all, internos]);

  // Lista visible del tab actual (con pendientes / búsqueda / sub-filtro)
  const visible = useMemo(() => {
    let list = tab === "choferes" && pendingMode ? pending : (bySegment[tab] || []);
    if (estadoFilter === "active") list = list.filter((u) => u.active !== false);
    // "blocked": bloqueado/inactivo según el segmento (interna usa teamStatus).
    if (estadoFilter === "blocked") {
      list = list.filter((u) =>
        tab === "interna" ? teamStatus(u) === "inactive" : u.active === false,
      );
    }
    // "pending": solo Gestión interna (invitado que aún no acepta).
    if (estadoFilter === "pending") list = list.filter((u) => teamStatus(u) === "pending");
    if (roleFilter) list = list.filter((u) => u?.role?.name === roleFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      // rut/dniNumber agregados al matcher: la columna nueva muestra RUT y
      // sería raro verlo sin poder buscarlo (paridad con el mockup).
      list = list.filter((u) =>
        `${u.name || ""} ${u.lastName || ""} ${u.email || ""} ${u.rut || ""} ${u?.dniData?.dniNumber || ""}`
          .toLowerCase()
          .includes(q)
      );
    }
    if (userSortField) {
      const asc = userSortDir === "asc";
      const name = (u) => `${u.name || ""} ${u.lastName || ""}`.trim().toLowerCase();
      const created = (u) => (u.createdAt ? new Date(u.createdAt).getTime() : 0);
      const cmp = {
        name: (a, b) => (asc ? name(a).localeCompare(name(b)) : name(b).localeCompare(name(a))),
        created: (a, b) => (asc ? created(a) - created(b) : created(b) - created(a)),
      }[userSortField];
      if (cmp) list = [...list].sort(cmp);
    }
    return list;
  }, [tab, pendingMode, pending, bySegment, roleFilter, search, estadoFilter, userSortField, userSortDir]);

  // Roles presentes en el segmento (para sub-filtro)
  const segmentRoles = useMemo(() => {
    const m = new Map();
    (bySegment[tab] || []).forEach((u) => {
      const n = u?.role?.name; if (!n) return;
      if (!m.has(n)) m.set(n, { name: n, label: u?.role?.name_public || n, count: 0 });
      m.get(n).count += 1;
    });
    return [...m.values()].sort((a, b) => b.count - a.count);
  }, [bySegment, tab]);

  // ── Detalle inline: usuario abierto + segmento al que pertenece ──
  // Se resuelve por _id contra las listas cargadas (todas comparten shape). Si
  // el id no aparece (borrado, ?user= inválido, aún cargando) se cae a la lista.
  const detailUser = useMemo(() => {
    if (!detailUserId) return null;
    return (
      (internos || []).find((x) => x._id === detailUserId) ||
      all.find((x) => x._id === detailUserId) ||
      pending.find((x) => x._id === detailUserId) ||
      null
    );
  }, [detailUserId, all, internos, pending]);

  const detailSeg = useMemo(() => {
    if (!detailUser) return null;
    if ((internos || []).some((x) => x._id === detailUser._id)) return "interna";
    // El ROL manda antes que la lista (un delivery_commerce_fleet es chofer:
    // heatmap por driverId, vehículo, documentos). Un rol de segmento
    // "clientes" acá significa deep-link viejo → redirect a Empresas (render).
    return roleSegment(detailUser?.role?.name) || "otros";
  }, [detailUser, internos]);

  const detailIsPendingDriver = useMemo(
    () => !!detailUser && pending.some((x) => x._id === detailUser._id) && !all.some((x) => x._id === detailUser._id),
    [detailUser, pending, all],
  );

  // ?user= que no resuelve con las listas ya cargadas (usuario eliminado, id
  // inválido, borrado por otro admin): limpiar el param para no arrastrar un
  // deep-link muerto en la URL y en cada navegación posterior. Esquina
  // asumida: un ?user= SIN tab=clientes de un usuario que solo existe en
  // users/clients tampoco resuelve acá (ese fetch ya no vive en este módulo) y
  // cae a la lista; los links reales del detalle de comercio siempre llevaban
  // tab=clientes, que redirige a Cuentas preservando el user.
  useEffect(() => {
    if (detailUserId && !loading && !detailUser) setDetailUserId("");
  }, [detailUserId, loading, detailUser, setDetailUserId]);

  // ── Acciones (reusan modales existentes) ──
  const closeAll = () => { setOpenBlock(false); setEditMode(false); setBlockMode(false); setSelectedUser(null); };
  const onView = (u) => { setRowMenu(null); setDetailUserId(u._id); };
  const onEdit = (u) => {
    setSelectedUser(u); setEditMode(true); setRowMenu(null);
    setOpenDelivery(true);
  };
  // OJO: BlockDeleteUserBeartrack solo sabe BLOQUEAR (block=true) o BORRAR
  // (block=false). No tiene camino de desbloqueo, así que acá solo se entra a
  // bloquear; habilitar va por EnableUserModal.
  const onBlock = (u) => { setSelectedUser(u); setBlockMode(true); setRowMenu(null); setOpenBlock(true); };
  const onDelete = (u) => { setSelectedUser(u); setBlockMode(false); setRowMenu(null); setOpenBlock(true); };
  const onEnable = (u, mode) => { setSelectedUser(u); setRowMenu(null); setEnableMode(mode); };
  // Las marcas de vendedor / supervisor de ventas son de usuarios de comercio:
  // viven con ellos en Empresas → Cuentas (CuentasUsuariosPanel).

  // ── Equipo interno: invitar / activar / desactivar / reenviar ──
  const openInviteModal = () => {
    // Preselecciona el rol de MENOR privilegio de los disponibles: si el que
    // invita no repara en el selector, el default no regala permisos.
    const leastPrivileged = assignableRoles[assignableRoles.length - 1];
    setInviteForm({ ...INITIAL_INVITE_FORM, role: leastPrivileged?.value || "" });
    setInviteErrors({});
    setOpenCreate(false);
    setOpenInvite(true);
  };

  const handleInviteChange = (key, value) => {
    setInviteForm((prev) => ({ ...prev, [key]: value }));
    setInviteErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validateInvite = () => {
    const errs = {};
    const name = inviteForm.name.trim();
    const lastName = inviteForm.lastName.trim();
    const phone = inviteForm.phone.trim();
    if (!name) errs.name = t("Nombre es obligatorio");
    else if (name.length > INVITE_NAME_MAX) errs.name = t("Máximo {{max}} caracteres", { max: INVITE_NAME_MAX });
    if (!lastName) errs.lastName = t("Apellido es obligatorio");
    else if (lastName.length > INVITE_NAME_MAX) errs.lastName = t("Máximo {{max}} caracteres", { max: INVITE_NAME_MAX });
    if (!inviteForm.email.trim()) errs.email = t("Email es obligatorio");
    else if (!EMAIL_REGEX.test(inviteForm.email)) errs.email = t("Email inválido");
    if (!phone) errs.phone = t("Teléfono es obligatorio");
    else if (phone.length < INVITE_PHONE_MIN) errs.phone = t("Mínimo {{min}} caracteres", { min: INVITE_PHONE_MIN });
    else if (phone.length > INVITE_PHONE_MAX) errs.phone = t("Máximo {{max}} caracteres", { max: INVITE_PHONE_MAX });
    if (!inviteForm.role) errs.role = t("Selecciona un rol");
    setInviteErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submitInvite = async () => {
    if (!validateInvite()) return;
    setInviting(true);
    const { data, error } = await createLogisticTeamUser({
      name: inviteForm.name.trim(),
      lastName: inviteForm.lastName.trim(),
      email: inviteForm.email.trim(),
      phone: inviteForm.phone.trim(),
      role: inviteForm.role,
    });
    setInviting(false);

    if (error) {
      // El backend devuelve { message, field } en 409 (email/teléfono duplicado).
      if (error.field) {
        setInviteErrors((prev) => ({ ...prev, [error.field]: error.message }));
        toast.error(error.message || t("No se pudo invitar al usuario"));
        return;
      }
      // 400 del ValidationPipe: `messages` trae una restricción por campo. Se
      // marca cada input con su texto traducido y el toast resume; si algún
      // mensaje no calzó con ningún campo (o ese campo no está en el form), se
      // muestra el mensaje del backend, que es el único que puede explicarlo.
      if (error.messages?.length) {
        const { errors: fieldErrors, unmatched } = mapValidationErrors(error.messages, {
          name: "name",
          lastName: "lastName",
          email: "email",
          phone: "phone",
          role: "role",
        });
        const translated = Object.entries(fieldErrors).reduce((acc, [key, message]) => {
          acc[key] = translateInviteConstraint(message, t);
          return acc;
        }, {});
        const marked = Object.keys(translated).length > 0;
        if (marked) setInviteErrors((prev) => ({ ...prev, ...translated }));
        if (marked && unmatched.length === 0) {
          toast.error(t("Revisa los campos marcados"));
          return;
        }
      }
      // Texto del backend si aporta algo mostrable; si no (403/500 genéricos,
      // red), el fallback TRADUCIDO — por eso no se usa `error.message`, que la
      // capa de API ya dejó en null justamente para no pisarlo con un literal.
      toast.error(apiBodyMessage(error) ?? t("No se pudo invitar al usuario"));
      return;
    }

    // El alta es exitosa aunque el correo no salga: hay que decirlo distinto,
    // porque en ese caso el invitado no recibió nada y hay que reenviarle.
    if (data?.invite?.sent === false) toast.warning(data?.message || t("Usuario creado, pero no pudimos enviar el correo."));
    else toast.success(data?.message || t("Usuario invitado"));
    setOpenInvite(false);
    fetchAll();
  };

  const onTeamToggle = async (u) => {
    setBusyId(u._id);
    setRowMenu(null);
    const action = u.active ? deactivateLogisticTeamUser : activateLogisticTeamUser;
    const { error } = await action(u._id);
    setBusyId(null);
    if (error) {
      toast.error(apiErrorMessage(error, t("Error al cambiar el estado")));
      return;
    }
    toast.success(u.active ? t("Usuario desactivado") : t("Usuario activado"));
    // Se actualiza solo la fila afectada: refetchear pondría toda la tabla en
    // skeleton por un cambio de un bit.
    setInternos((prev) => (prev || []).map((x) => (x._id === u._id ? { ...x, active: !u.active } : x)));
  };

  const onTeamResend = async (u) => {
    setBusyId(u._id);
    setRowMenu(null);
    const { data, error } = await resendLogisticTeamInvite(u._id);
    setBusyId(null);
    if (error) {
      toast.error(apiErrorMessage(error, t("No se pudo reenviar la invitación")));
      return;
    }
    if (data?.invite?.sent === false) toast.warning(t("No pudimos enviar el correo. Revisa la dirección."));
    else toast.success(t("Invitación reenviada"));
  };

  // En "Gestión interna" el estado tiene tres valores (un invitado que aún no
  // acepta no es lo mismo que uno bloqueado); en el resto de los segmentos se
  // mantiene el binario Activo/Bloqueado de siempre.
  const StatusBadge = ({ u, seg }) => {
    // En la cola "Por aceptar" el binario Activo/Bloqueado miente: un
    // autoregistro sin aprobar no está bloqueado, está pendiente.
    if (seg === "choferes" && pendingMode) {
      return <Badge tone="warning" dot>{t("Pendiente")}</Badge>;
    }
    if (seg === "interna") {
      const status = teamStatus(u);
      if (status === "pending") return <Badge tone="warning" dot>{t("Pendiente")}</Badge>;
      if (status === "inactive") return <Badge tone="neutral" dot>{t("Inactivo")}</Badge>;
      return <Badge tone="success" dot>{t("Activo")}</Badge>;
    }
    const blocked = u.active === false;
    return <Badge tone={blocked ? "neutral" : "success"} dot>{blocked ? t("Bloqueado") : t("Activo")}</Badge>;
  };

  // Ítems del menú de fila, compartidos por la tabla desktop y las cards mobile.
  // En "Gestión interna" el activar/desactivar va por los endpoints scopeados de
  // users/logistic-team, NO por PATCH /users/:id, que no valida que el usuario
  // pertenezca al operador del que llama.
  //
  // En la cola "Por aceptar" la única acción de estado es aceptar: bloquear a
  // alguien que todavía no entra no significa nada, y el borrado no se ofrece
  // porque un pendiente que no queremos simplemente se deja en la cola.
  const RowMenuItems = ({ u, seg }) => {
    const blocked = u.active === false;
    const isTeam = seg === "interna" && canManageTeam;
    const isPendingDriver = seg === "choferes" && pendingMode;
    const status = teamStatus(u);
    const itemCls = "w-full flex items-center gap-2 rounded-md px-3 py-2 text-sm text-fg hover:bg-bg-muted disabled:opacity-50";
    return (
      <>
        <button type="button" className={itemCls} onClick={() => onView(u)}><Eye className="w-4 h-4" />{t("Ver")}</button>
        <button type="button" className={itemCls} onClick={() => onEdit(u)}><Pencil className="w-4 h-4" />{t("Editar")}</button>
        {isPendingDriver ? (
          <button type="button" className={itemCls} onClick={() => onEnable(u, "accept")}>
            <UserCheck className="w-4 h-4" />{t("Aceptar repartidor")}
          </button>
        ) : isTeam ? (
          <>
            {status === "pending" && (
              <button type="button" className={itemCls} disabled={busyId === u._id} onClick={() => onTeamResend(u)}>
                <Send className="w-4 h-4" />{t("Reenviar invitación")}
              </button>
            )}
            {canAssignRole && (
              <button
                type="button"
                className={itemCls}
                disabled={u._id === user?.id}
                title={u._id === user?.id ? t("No puedes cambiar tu propio rol.") : undefined}
                onClick={() => { setRowMenu(null); setRoleChangeTarget(u); }}
              >
                <UserCog className="w-4 h-4" />{t("Cambiar rol")}
              </button>
            )}
            <button type="button" className={itemCls} disabled={busyId === u._id} onClick={() => onTeamToggle(u)}>
              {u.active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              {u.active ? t("Desactivar") : t("Activar")}
            </button>
          </>
        ) : blocked ? (
          <button type="button" className={itemCls} onClick={() => onEnable(u, "unblock")}>
            <Unlock className="w-4 h-4" />{t("Desbloquear")}
          </button>
        ) : (
          <button type="button" className={itemCls} onClick={() => onBlock(u)}>
            <Lock className="w-4 h-4" />{t("Bloquear")}
          </button>
        )}
        {!isPendingDriver && (
          <button type="button" className="w-full flex items-center gap-2 rounded-md px-3 py-2 text-sm text-status-error hover:bg-status-error-soft" onClick={() => onDelete(u)}><Trash2 className="w-4 h-4" />{t("Eliminar")}</button>
        )}
      </>
    );
  };

  // Dropdown de acciones portaleado a <body> con coordenadas fixed (ver
  // openRowMenu). Mantiene la clase um-rowmenu para el cierre por click afuera.
  // stopPropagation: el portal burbujea por el árbol de React, y sin él un click
  // en el menú dispararía también el onView() de la fila que lo montó.
  const RowMenuPortal = ({ u, seg }) => {
    if (!rowMenuPos) return null;
    return createPortal(
      <div
        className="um-rowmenu fixed z-50 w-52 rounded-lg border border-border bg-bg shadow-lg p-1"
        style={rowMenuPos}
        onClick={(e) => e.stopPropagation()}
      >
        <RowMenuItems u={u} seg={seg} />
      </div>,
      document.body
    );
  };

  const tabContentDelivery = [<CreateDeliveryBeartrack key="d" closeModal={setOpenDelivery} cerrarModal={closeAll} fetch={fetchAll} user={selectedUser} edit={editMode} />];
  const tabContentBlock = [<BlockDeleteUserBeartrack key="bd" closeModal={setOpenBlock} cerrarModal={closeAll} fetch={fetchAll} user={selectedUser} block={blockMode} />];

  // Título/bajada del card de la tabla, DERIVADOS del estado: con la cola
  // "Por aceptar" activa un título genérico del segmento mentiría.
  const cardHeader = (seg) => {
    if (seg === "choferes" && pendingMode) {
      return {
        title: t("Choferes por aceptar"),
        desc: t("Autoregistros esperando aprobación del operador"),
      };
    }
    return {
      choferes: { title: t("Todos los choferes"), desc: t("Repartidores de la operación en una sola vista") },
      interna: { title: t("Equipo interno"), desc: t("Administradores, supervisores y soporte del operador") },
    }[seg];
  };

  const TABS = [
    { id: "choferes", label: t("Choferes"), icon: Truck },
    { id: "interna", label: t("Gestión interna"), icon: Shield },
    ...(canReadRoles ? [{ id: "roles", label: t("Roles y permisos"), icon: ShieldCheck }] : []),
  ];

  const counts = {
    choferes: bySegment.choferes.length,
    interna: bySegment.interna.length,
  };

  // ── Redirects de compatibilidad hacia Empresas → Cuentas ──
  // (a) URL vieja del tab clientes; (b) deep-link ?user= de un usuario de
  // comercio (KAM/client del tenant siguen resolviendo contra `all`). En ambos
  // casos el detalle/las acciones de comercio ya no existen en este módulo.
  if (legacyClientesTab || (detailUser && detailSeg === "clientes")) {
    const suffix = detailUserId ? `&user=${detailUserId}` : "";
    return <Navigate to={`/admin/contracts?tab=cuentas${suffix}`} replace />;
  }

  return (
    <div className="w-full min-w-0 p-4 sm:p-6 space-y-5">
      {detailUser ? (
        <UserDetail
          user={detailUser}
          seg={detailSeg}
          isPendingDriver={detailIsPendingDriver}
          canManageTeam={canManageTeam}
          busyId={busyId}
          onBack={() => setDetailUserId("")}
          onEdit={onEdit}
          onBlock={onBlock}
          onDelete={onDelete}
          onEnable={onEnable}
          onTeamToggle={onTeamToggle}
          onTeamResend={onTeamResend}
        />
      ) : detailUserId && loading ? (
        // Deep-link ?user= mientras cargan las listas: skeleton con la forma
        // del detalle, no un flash de la lista completa.
        <div className="space-y-4" aria-hidden="true">
          <div className="h-32 rounded-lg bg-bg-subtle animate-pulse" />
          <div className="h-44 rounded-lg bg-bg-subtle animate-pulse" />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="h-48 rounded-lg bg-bg-subtle animate-pulse" />
            <div className="h-48 rounded-lg bg-bg-subtle animate-pulse" />
          </div>
        </div>
      ) : (
        <>
      <PageHeader
        icon={<UsersIcon className="w-6 h-6 text-fg-muted" />}
        title={t("Usuarios")}
        subtitle={t("Choferes y equipo interno del operador en un solo lugar.")}
      />

      <Tabs value={tab} onValueChange={(v) => { setUserParams({ tab: v, role: "", pending: false, q: "" }); setEstadoFilter(""); setUserSortField(""); }}>
        <TabsList ariaLabel={t("Segmentos de usuarios")}>
          {TABS.map((x) => (
            <TabsTrigger key={x.id} value={x.id} icon={<x.icon className="w-4 h-4" />}>{x.label}</TabsTrigger>
          ))}
        </TabsList>

        {/* ── Tabs de personas ── */}
        {["choferes", "interna"].map((seg) => (
          <TabsContent key={seg} value={seg} className="mt-4 space-y-4">
            {tab === seg && (
              <>
                {/* Stats */}
                {/* max-w-6xl: sin tope, en pantallas anchas cada Stat se estira
                    ~430px+ y queda llena de aire; con el tope cada card queda
                    ~w-72, el mismo ancho fijo que usan los Stat del hub Empresas. */}
                <div className="grid max-w-6xl grid-cols-2 sm:grid-cols-4 gap-3">
                  <Stat
                    label={t("En este segmento")}
                    value={loading ? "—" : counts[seg]}
                    icon={TABS.find((x) => x.id === seg).icon}
                    tone="brand"
                    active={!estadoFilter && !pendingMode && !roleFilter && !search}
                    onClick={() => { setUserParams({ q: "", role: "", pending: false }, { push: false }); setEstadoFilter(""); }}
                  />
                  <Stat
                    label={t("Activos")}
                    value={loading ? "—" : (bySegment[seg] || []).filter((u) => u.active !== false).length}
                    icon={Check}
                    tone="success"
                    hint={loading ? null : t("de {{n}} en el segmento", { n: counts[seg] })}
                    active={activeOnly}
                    onClick={() => { setEstadoFilter(activeOnly ? "" : "active"); if (!activeOnly) setPendingMode(false); }}
                  />
                  {seg === "choferes" && (
                    <Stat label={t("Por aceptar")} value={loading ? "—" : pending.length} icon={UserPlus} tone="warning" active={pendingMode} onClick={() => { setPendingMode((v) => !v); if (!pendingMode) setEstadoFilter(""); }} />
                  )}
                  {seg === "interna" && (
                    <Stat
                      label={t("Pendientes")}
                      value={loading ? "—" : (bySegment.interna || []).filter((u) => teamStatus(u) === "pending").length}
                      icon={Clock}
                      tone="warning"
                    />
                  )}
                  <Stat label={t("Roles distintos")} value={loading ? "—" : segmentRoles.length} icon={Layers} tone="neutral" />
                </div>

                {seg === "interna" && internos === null && (
                  <PendingBackendNotice>
                    {t("No pudimos cargar el equipo interno; se muestran solo los usuarios activos del listado general y las acciones de gestión están deshabilitadas. Reintenta en unos minutos.")}
                  </PendingBackendNotice>
                )}

                {/* Card con header propio (patrón mockup): título derivado del
                    estado + CTA "Crear usuario", y la fila de filtros adentro.
                    Header y filtros viven FUERA del ternario loading/empty/tabla
                    para no desaparecer durante la carga ni con la lista vacía. */}
                <Card padding="none" className="overflow-hidden">
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
                    <div className="min-w-0">
                      <h2 className="text-base font-semibold text-fg">{cardHeader(seg).title}</h2>
                      <p className="text-xs text-fg-muted">{cardHeader(seg).desc}</p>
                    </div>
                    <div className="um-create shrink-0">
                      <Button
                        iconLeft={<Plus className="w-4 h-4" />}
                        onClick={toggleCreateMenu}
                        aria-haspopup="menu"
                        aria-expanded={openCreate}
                      >
                        {t("Crear usuario")}
                      </Button>
                      {openCreate && createMenuPos && createPortal(
                        <div
                          className="um-create fixed z-50 w-72 rounded-xl border border-border bg-bg p-1.5 shadow-elevated"
                          style={createMenuPos}
                          role="menu"
                          aria-label={t("Crear usuario")}
                        >
                          {/* Los choferes se dan de alta por el registro público
                              co-brandeado, no por un modal del panel: ese flujo
                              captura los datos propios de cada uno y el `?from=`
                              preasocia al operador. El modal de creación sigue
                              existiendo para EDITAR. El alta de usuarios de
                              comercio vive en Empresas → Cuentas. */}
                          <a href={registerUrl("drivers")} target="_blank" rel="noopener noreferrer" role="menuitem"
                            className="w-full flex items-start gap-2.5 rounded-lg px-3 py-2.5 text-left hover:bg-bg-muted"
                            onClick={() => setOpenCreate(false)}>
                            <Truck className="w-4 h-4 mt-0.5 text-status-info" />
                            <span><span className="block text-sm font-medium text-fg inline-flex items-center gap-1">{t("Crear chofer")}<ExternalLink className="w-3 h-3 text-fg-subtle" /></span>
                              <span className="block text-xs text-fg-muted">{t("Abre el registro público de repartidores de tu empresa.")}</span></span>
                          </a>
                          {canManageTeam ? (
                            <button type="button" role="menuitem" className="w-full flex items-start gap-2.5 rounded-lg px-3 py-2.5 text-left hover:bg-bg-muted"
                              onClick={openInviteModal}>
                              <ShieldCheck className="w-4 h-4 mt-0.5 text-brand-600" />
                              <span><span className="block text-sm font-medium text-fg">{t("Invitar usuario interno")}</span>
                                <span className="block text-xs text-fg-muted">{t("Administrador o soporte de tu empresa.")}</span></span>
                            </button>
                          ) : loading ? null : (
                            <div className="px-3 py-2 text-xs text-fg-subtle border-t border-border mt-1">
                              {t("Solo un administrador de la empresa puede invitar usuarios internos.")}
                            </div>
                          )}
                        </div>,
                        document.body,
                      )}
                    </div>
                  </div>

                  {/* Filtros: búsqueda + control segmentado de roles (oculto en
                      la cola "Por aceptar", donde un solo botón se vería roto). */}
                  <div className="flex flex-wrap items-center gap-2 border-b border-border px-5 py-3">
                    <div className="relative w-full sm:w-72">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-muted pointer-events-none" />
                      <input type="search" value={search} onChange={(e) => setSearch(e.target.value)}
                        placeholder={t("Buscar por nombre, email o RUT…")}
                        aria-label={t("Buscar usuarios")}
                        className="h-9 w-full rounded-lg border border-border bg-bg pl-9 pr-3 text-sm text-fg focus:outline-none focus:ring-2 focus:ring-brand-500" />
                    </div>
                    {!(seg === "choferes" && pendingMode) && (
                      <div
                        className="flex max-w-full items-center overflow-x-auto rounded-lg border border-border bg-bg-muted/50 p-0.5"
                        role="group"
                        aria-label={t("Filtrar por rol")}
                      >
                        <button type="button" aria-pressed={roleFilter === ""} onClick={() => setRoleFilter("")}
                          className={cn("h-8 shrink-0 whitespace-nowrap rounded-md px-3 text-sm font-medium transition-colors inline-flex items-center gap-1.5",
                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                            roleFilter === "" ? "bg-bg text-fg shadow-card" : "text-fg-muted hover:text-fg")}>
                          {t("Todos")} <span className="text-xs text-fg-muted tabular-nums">{(bySegment[seg] || []).length}</span>
                        </button>
                        {segmentRoles.map((r) => (
                          <button key={r.name} type="button" aria-pressed={roleFilter === r.name}
                            onClick={() => setRoleFilter(r.name === roleFilter ? "" : r.name)}
                            className={cn("h-8 shrink-0 whitespace-nowrap rounded-md px-3 text-sm font-medium transition-colors inline-flex items-center gap-1.5",
                              "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                              roleFilter === r.name ? "bg-bg text-fg shadow-card" : "text-fg-muted hover:text-fg")}>
                            {humanizeRole(r.label)} <span className="text-xs text-fg-muted tabular-nums">{r.count}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {/* Estado + orden (client-side; toda la data del segmento ya está cargada). */}
                    {!pendingMode && (
                      <Select
                        aria-label={t("Filtrar por estado")}
                        value={estadoFilter}
                        onChange={(e) => setEstadoFilter(e.target.value)}
                        // h-9 = misma altura que el resto del toolbar (search y
                        // control segmentado); w-48 para que "Todos los estados"
                        // no quede pegado al chevron.
                        className="h-9 w-48"
                        options={[
                          { value: "", label: t("Todos los estados") },
                          { value: "active", label: t("Activos") },
                          {
                            value: "blocked",
                            label: seg === "interna" ? t("Inactivos") : t("Bloqueados"),
                          },
                          ...(seg === "interna" ? [{ value: "pending", label: t("Pendientes") }] : []),
                        ]}
                      />
                    )}
                    <SortControl
                      ariaLabel={t("Ordenar usuarios")}
                      field={userSortField}
                      dir={userSortDir}
                      onFieldChange={setUserSortField}
                      onDirChange={setUserSortDir}
                      // w-56: con w-48 "Orden predeterminado" se truncaba.
                      selectClassName="h-9 w-56"
                      buttonClassName="h-9 w-9"
                      fields={[
                        { value: "name", label: t("Por nombre"), defaultDir: "asc" },
                        { value: "created", label: t("Por fecha de registro"), defaultDir: "desc" },
                      ]}
                    />
                  </div>

                  {loading ? (
                    // Edge-to-edge (border-0) para coincidir con la tabla real que
                    // llena la Card sin padding ni borde interno.
                    <TableSkeleton rows={8} columns={6} className="border-0 rounded-none" />
                  ) : visible.length === 0 ? (
                    <EmptyState icon={<UsersIcon />} title={t("Sin usuarios")} description={search || roleFilter || estadoFilter ? t("Nada coincide con el filtro.") : t("No hay usuarios en este segmento todavía.")} />
                  ) : isMobile ? (
                    <div className="p-3 space-y-3">
                      {visible.map((u) => {
                        const blocked = u.active === false;
                        return (
                          <div key={u._id} className="rounded-xl border border-border bg-bg p-4 space-y-3 cursor-pointer"
                            onClick={() => onView(u)}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <Avatar initials={initialsOf(u.name, u.lastName)} size="sm" tone={blocked ? "neutral" : "brand"} />
                                <div className="min-w-0">
                                  <div className="text-sm font-medium text-fg truncate">{u.name} {u.lastName}</div>
                                  <div className="text-xs text-fg-muted truncate">{u.email || "—"}</div>
                                </div>
                              </div>
                              <div className="um-rowmenu relative shrink-0">
                                <button type="button" aria-label={t("Acciones")} className="p-1.5 rounded-md text-fg-muted hover:bg-bg-muted hover:text-fg"
                                  onClick={(e) => openRowMenu(e, u._id)}>
                                  <MoreVertical className="w-4 h-4" />
                                </button>
                                {rowMenu === u._id && <RowMenuPortal u={u} seg={seg} />}
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge tone={roleTone(u?.role?.name)}>{humanizeRole(u?.role?.name_public || u?.role?.name || "—")}</Badge>
                              {u?.customRole?.name && <Badge tone="info">{u.customRole.name}</Badge>}
                              <StatusBadge u={u} seg={seg} />
                              {/* Vehículo del chofer (tolera ausencia hasta que el backend lo exponga). */}
                              {seg === "choferes" && u.vehicleType && (
                                <Badge tone="neutral">{driverVehicleLabel(u.vehicleType, t)}</Badge>
                              )}
                              <span className="text-xs text-fg-muted ml-auto truncate">
                                {u.createdAt ? formatRelative(u.createdAt) : "—"}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <THead>
                          <tr>
                            <TH>{t("Usuario")}</TH>
                            <TH>{t("Tipo")}</TH>
                            <TH>{t("RUT / Teléfono")}</TH>
                            {seg === "choferes" && !pendingMode ? (
                              <TH>{t("Vehículo")}</TH>
                            ) : (
                              <TH>{t("Registro")}</TH>
                            )}
                            <TH>{t("Estado")}</TH>
                            <TH className="w-10"></TH>
                          </tr>
                        </THead>
                        <TBody>
                          {visible.map((u) => {
                            const blocked = u.active === false;
                            const rut = u.rut || u?.dniData?.dniNumber || null;
                            const vehicleType = u.vehicleType ?? u?.vehicleData?.vehicleType;
                            const plate = u?.vehicleData?.vehiclePlate;
                            return (
                              <TR key={u._id} className="cursor-pointer" onClick={() => onView(u)}>
                                <TD>
                                  <div className="flex items-center gap-2.5">
                                    <Avatar initials={initialsOf(u.name, u.lastName)} size="sm" tone={blocked ? "neutral" : "brand"} />
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-1.5">
                                        <span className="text-sm font-medium text-fg truncate">{u.name} {u.lastName}</span>
                                      </div>
                                      <div className="text-xs text-fg-muted truncate">{u.email || "—"}</div>
                                    </div>
                                  </div>
                                </TD>
                                <TD>
                                  <div className="flex flex-wrap items-center gap-1">
                                    <Badge tone={roleTone(u?.role?.name)}>
                                      {humanizeRole(u?.role?.name_public || u?.role?.name || "—")}
                                    </Badge>
                                    {u?.customRole?.name && <Badge tone="info">{u.customRole.name}</Badge>}
                                  </div>
                                </TD>
                                <TD>
                                  {rut ? (
                                    <span className="text-sm font-mono text-fg-muted whitespace-nowrap">{rut}</span>
                                  ) : (
                                    <span className="text-sm text-fg-muted whitespace-nowrap">{u.phone || "—"}</span>
                                  )}
                                </TD>
                                {seg === "choferes" && !pendingMode ? (
                                  <TD>
                                    {vehicleType ? (
                                      <span className="text-sm text-fg whitespace-nowrap">
                                        {driverVehicleLabel(vehicleType, t)}
                                        {plate && (
                                          <>
                                            {" · "}
                                            <span className="font-mono text-fg-muted">{plate}</span>
                                          </>
                                        )}
                                      </span>
                                    ) : (
                                      <span className="text-sm text-fg-subtle">—</span>
                                    )}
                                  </TD>
                                ) : (
                                  <TD><span className="text-xs text-fg-muted whitespace-nowrap">{u.createdAt ? formatRelative(u.createdAt) : "—"}</span></TD>
                                )}
                                <TD>
                                  <StatusBadge u={u} seg={seg} />
                                </TD>
                                <TD>
                                  <div className="um-rowmenu relative flex justify-end">
                                    <button type="button" aria-label={t("Acciones")} className="p-1.5 rounded-md text-fg-muted hover:bg-bg-muted hover:text-fg"
                                      onClick={(e) => openRowMenu(e, u._id)}>
                                      <MoreVertical className="w-4 h-4" />
                                    </button>
                                    {rowMenu === u._id && <RowMenuPortal u={u} seg={seg} />}
                                  </div>
                                </TD>
                              </TR>
                            );
                          })}
                        </TBody>
                      </Table>
                    </div>
                  )}
                </Card>
              </>
            )}
          </TabsContent>
        ))}

        {/* ── Tab Roles y permisos (R3.2) ── */}
        <TabsContent value="roles" className="mt-4 space-y-4">
          {tab === "roles" && <RolesPermisos />}
        </TabsContent>
      </Tabs>
        </>
      )}

      {/* ── Modales (reusan los componentes existentes; montados también con el
          detalle abierto, que dispara Editar/Bloquear/Eliminar desde su banda) ── */}
      <Modal size="md" open={openBlock} setOpen={() => { setOpenBlock(false); setBlockMode(false); setSelectedUser(null); }} body={<PackageTabs tabContent={tabContentBlock} />} />
      <Modal size="md" open={openDelivery} setOpen={() => { setOpenDelivery(false); if (!editMode) setSelectedUser(null); }} body={<PackageTabs tabContent={tabContentDelivery} />} />

      {/* ── Habilitar usuario: aceptar un pendiente / desbloquear ── */}
      <EnableUserModal
        open={!!enableMode}
        mode={enableMode || "accept"}
        user={selectedUser}
        logisticCompanyId={logisticCompanyId}
        onClose={() => { setEnableMode(null); setSelectedUser(null); }}
        onDone={fetchAll}
      />

      {/* ── Cambiar rol de un integrante del equipo interno (role:assign) ── */}
      <ChangeRoleModal
        open={!!roleChangeTarget}
        member={roleChangeTarget}
        currentUserId={user?.id}
        onClose={() => setRoleChangeTarget(null)}
        onChanged={(memberId, data) => {
          setRoleChangeTarget(null);
          // Solo la fila afectada: refetchear pondría toda la tabla en skeleton.
          setInternos((prev) => (prev || []).map((x) => (
            x._id === memberId ? { ...x, role: data?.role ?? x.role, customRole: data?.customRole ?? null } : x
          )));
        }}
      />

      {/* ── Invitar usuario interno (equipo de la empresa logística) ── */}
      <UiModal
        open={openInvite}
        onClose={() => !inviting && setOpenInvite(false)}
        size="md"
        title={t("Invitar usuario interno")}
        footer={
          <div className="flex items-center justify-end w-full gap-2">
            <Button variant="outline" onClick={() => setOpenInvite(false)} disabled={inviting}>
              {t("Cancelar")}
            </Button>
            <Button onClick={submitInvite} loading={inviting} iconLeft={<Send className="w-4 h-4" />}>
              {inviting ? t("Enviando…") : t("Enviar invitación")}
            </Button>
          </div>
        }
      >
        <p className="text-[13px] text-fg-muted mb-4">
          {t("Le enviaremos un correo para que active su cuenta y elija su contraseña. El enlace vence en 7 días.")}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label={t("Nombre")} required error={inviteErrors.name}>
            <Input value={inviteForm.name} onChange={(e) => handleInviteChange("name", e.target.value)} invalid={!!inviteErrors.name} />
          </FormField>
          <FormField label={t("Apellido")} required error={inviteErrors.lastName}>
            <Input value={inviteForm.lastName} onChange={(e) => handleInviteChange("lastName", e.target.value)} invalid={!!inviteErrors.lastName} />
          </FormField>
          <FormField label={t("Email")} required error={inviteErrors.email}>
            <Input
              type="email"
              value={inviteForm.email}
              onChange={(e) => handleInviteChange("email", e.target.value)}
              invalid={!!inviteErrors.email}
              placeholder="colega@tuempresa.com"
            />
          </FormField>
          <FormField label={t("Teléfono")} required error={inviteErrors.phone}>
            <Input
              value={inviteForm.phone}
              onChange={(e) => handleInviteChange("phone", e.target.value)}
              invalid={!!inviteErrors.phone}
              placeholder="+56912345678"
            />
          </FormField>
          <div className="md:col-span-2">
            <Select
              label={t("Rol")}
              value={inviteForm.role}
              onChange={(e) => handleInviteChange("role", e.target.value)}
              error={inviteErrors.role}
              hint={assignableRoles.find((r) => r.value === inviteForm.role)?.hint}
              placeholder={t("Selecciona un rol")}
              options={assignableRoles.map((r) => ({ value: r.value, label: t(r.label) }))}
            />
          </div>
        </div>
      </UiModal>
    </div>
  );
}
